"use client";
import React, { useEffect, useState } from "react";
import { Clock, CircleHelp, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
const getRemainingKeepMs = (expiresAt: number) =>
  Math.max(0, expiresAt - Date.now());
const formatKeepRemainingLabel = (
  remainingMs: number | null,
  hasProblemIds: boolean,
): string => {
  if (remainingMs === null) return hasProblemIds ? "만료됨" : "저장 대기";
  if (remainingMs <= 0) return "만료됨";

  const totalMinutes = Math.ceil(remainingMs / (60 * 1000));
  if (totalMinutes >= 60) return "남은 1시간";
  return `남은 ${totalMinutes}분`;
};

export const KeepProblemsHelpButton = React.memo(
  function KeepProblemsHelpButton() {
    return (
      <Tooltip tapToOpen>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="문제 유지 설명 보기"
            className="!size-8 !w-8 shrink-0"
          >
            <CircleHelp className="h-4 w-4" aria-hidden="true" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-64 text-xs leading-relaxed">
          켜두면 이 페이지를 새로고침해도 1시간 동안 같은 문제가 유지돼요.
          연장을 누르면 남은 시간이 다시 1시간으로 늘어납니다.
        </TooltipContent>
      </Tooltip>
    );
  },
);

export const KeepProblemsActiveControls = React.memo(
  function KeepProblemsActiveControls({
    readRemaining,
    hasProblemIds,
    onExtend,
  }: {
    readRemaining: () => number | null;
    hasProblemIds: boolean;
    onExtend: () => number | null;
  }) {
    const [remainingMs, setRemainingMs] = useState<number | null>(() =>
      readRemaining(),
    );

    const updateRemaining = React.useCallback(() => {
      setRemainingMs(readRemaining());
    }, [readRemaining]);

    useEffect(() => {
      updateRemaining();
      const intervalId = window.setInterval(updateRemaining, 15 * 1000);
      return () => window.clearInterval(intervalId);
    }, [updateRemaining]);

    const keepRemainingLabel = formatKeepRemainingLabel(
      remainingMs,
      hasProblemIds,
    );

    const handleExtend = () => {
      const expiresAt = onExtend();
      if (expiresAt === null) {
        updateRemaining();
        return;
      }
      setRemainingMs(getRemainingKeepMs(expiresAt));
    };

    return (
      <div className="flex min-w-0 items-center gap-1">
        <span
          data-keep-status
          role="status"
          aria-live="polite"
          aria-atomic="true"
          aria-label={`문제 유지 ${keepRemainingLabel}`}
          className="inline-flex h-8 min-w-0 flex-1 items-center justify-start whitespace-nowrap rounded-md border border-border bg-background px-2.5 text-xs font-medium text-muted-foreground"
        >
          <Clock className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          {keepRemainingLabel}
        </span>
        <KeepProblemsHelpButton />
        {hasProblemIds && (
          <Button
            onClick={handleExtend}
            variant="outline"
            size="sm"
            aria-label="문제 유지 1시간 연장"
            className="!w-auto shrink-0"
          >
            <RefreshCw className="h-4 w-4 mr-1" aria-hidden="true" />
            연장
          </Button>
        )}
      </div>
    );
  },
);
